import type { PricingTier, SiteSettings } from "@aurumiq/cms-schema";
import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  CmsDraftPutInput,
  CmsMedia,
  CmsPageCreateInput,
  CmsPageDetail,
  CmsPageKind,
  CmsPageSummary,
  CmsPreviewToken,
  CmsUploadUrl,
  Paginated,
} from "@/lib/types";

const BASE = "/api/v1/admin/cms";

export const cmsKeys = {
  pages: ["admin", "cms", "pages"] as const,
  pageList: (kind: string) => ["admin", "cms", "pages", { kind }] as const,
  page: (id: string) => ["admin", "cms", "pages", id] as const,
  media: (page: number) => ["admin", "cms", "media", { page }] as const,
  site: ["admin", "cms", "site"] as const,
  pricing: ["admin", "cms", "pricing"] as const,
};

function useToastMutation<TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
  keys: readonly (readonly unknown[])[],
  success?: string | ((data: TData, vars: TVars) => string | undefined),
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      for (const k of keys) qc.invalidateQueries({ queryKey: k });
      const msg = typeof success === "function" ? success(data, vars) : success;
      if (msg) toast.success(msg);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

/* ---------- pages ---------- */

export function useCmsPages(kind: CmsPageKind | "" = "") {
  return useQuery({
    queryKey: cmsKeys.pageList(kind),
    queryFn: () => rawApi.get<Paginated<CmsPageSummary>>(`${BASE}/pages${qs({ kind })}`),
  });
}

export function useCmsPage(id: string) {
  return useQuery({
    queryKey: cmsKeys.page(id),
    queryFn: () => rawApi.get<CmsPageDetail>(`${BASE}/pages/${id}`),
  });
}

export function useCreatePage() {
  return useToastMutation(
    (input: CmsPageCreateInput) => rawApi.post<CmsPageDetail>(`${BASE}/pages`, input),
    [cmsKeys.pages],
    (p) => `“${p.title}” created`,
  );
}

export function useSaveDraft(id: string) {
  return useToastMutation(
    (input: CmsDraftPutInput) => rawApi.put<CmsPageDetail>(`${BASE}/pages/${id}/draft`, input),
    [cmsKeys.pages],
    "Draft saved",
  );
}

export function usePublishPage(id: string) {
  return useToastMutation(
    (input: { revision_id?: string; reason?: string }) =>
      rawApi.post<CmsPageDetail>(`${BASE}/pages/${id}/publish`, input),
    [cmsKeys.pages],
    "Published",
  );
}

export function useRevertPage(id: string) {
  return useToastMutation(
    (revision_id: string) =>
      rawApi.post<CmsPageDetail>(`${BASE}/pages/${id}/revert`, { revision_id }),
    [cmsKeys.pages],
    "Draft reverted to the selected revision",
  );
}

export function useDeletePage() {
  return useToastMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      rawApi.delete<void>(`${BASE}/pages/${id}${qs({ reason })}`),
    [cmsKeys.pages],
    "Page deleted",
  );
}

export function usePreviewToken(id: string) {
  return useMutation({
    mutationFn: () => rawApi.post<CmsPreviewToken>(`${BASE}/pages/${id}/preview-token`),
    onError: (err) => toast.error(`Preview unavailable: ${errorMessage(err)}`),
  });
}

/* ---------- media ---------- */

export function useMedia(page = 1) {
  return useQuery({
    queryKey: cmsKeys.media(page),
    queryFn: () => rawApi.get<Paginated<CmsMedia>>(`${BASE}/media${qs({ page })}`),
  });
}

/** PUT bytes to a presigned URL with upload progress (fetch has no progress events). */
async function putWithProgress(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (pct: number) => void,
) {
  const bytes = await file.arrayBuffer();
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(bytes);
  });
}

export interface UploadVars {
  file: File;
  alt: string;
  onProgress?: (pct: number) => void;
}

/** upload-url → PUT bytes → finalise. */
export function useUploadMedia() {
  return useToastMutation(
    async ({ file, alt, onProgress }: UploadVars) => {
      const u = await rawApi.post<CmsUploadUrl>(`${BASE}/media/upload-url`, {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
      });
      await putWithProgress(u.upload_url, u.upload_headers ?? {}, file, onProgress ?? (() => {}));
      return rawApi.post<CmsMedia>(`${BASE}/media`, { media_id: u.media_id, alt });
    },
    [["admin", "cms", "media"]],
    (m) => `${m.filename} uploaded`,
  );
}

export function usePatchMedia() {
  return useToastMutation(
    ({ id, alt }: { id: string; alt: string }) =>
      rawApi.patch<CmsMedia>(`${BASE}/media/${id}`, { alt }),
    [["admin", "cms", "media"]],
    "Alt text saved",
  );
}

export function useDeleteMedia() {
  return useToastMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      rawApi.delete<void>(`${BASE}/media/${id}${qs({ reason })}`),
    [["admin", "cms", "media"]],
    "Media deleted",
  );
}

/* ---------- site / pricing ---------- */

export function useSiteSettings() {
  return useQuery({
    queryKey: cmsKeys.site,
    queryFn: () => rawApi.get<Partial<SiteSettings>>(`${BASE}/site`),
  });
}

export function usePutSiteSettings() {
  return useToastMutation(
    (input: SiteSettings) => rawApi.put<SiteSettings>(`${BASE}/site`, input),
    [cmsKeys.site],
    "Site settings saved",
  );
}

export function usePricingTiers() {
  return useQuery({
    queryKey: cmsKeys.pricing,
    queryFn: () => rawApi.get<{ tiers: PricingTier[] }>(`${BASE}/pricing`),
  });
}

export function usePutPricingTiers() {
  return useToastMutation(
    (input: { tiers: PricingTier[]; reason?: string }) =>
      rawApi.put<{ tiers: PricingTier[] }>(`${BASE}/pricing`, input),
    [cmsKeys.pricing],
    "Pricing tiers saved",
  );
}
